// app/src/main.tsx
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App";
// Tabler (Bootstrap 5 admin theme) is the framework layer. global.css follows so the
// design tokens + existing Tailwind utilities still win where names overlap; tabler-brand.css
// is last and rebrands Tabler (red primary, brand fonts) + bridges dark mode.
import "./styles/tabler.purged.css"; // generated: scripts/purge-tabler.mjs (prebuild/predev)
import "./styles/global.css";
import "./styles/tabler-brand.css";
import { initTheme } from "./lib/theme";
import { showUpdatePrompt } from "./lib/pwa-update";

initTheme();

// With code-split routes a chunk can 404 if a new build shipped while this tab stayed
// open. Vite fires "vite:preloadError" when a dynamic import fails — reload once (rate
// limited so it can never loop) to pull the current build instead of white-screening.
window.addEventListener("vite:preloadError", () => {
  const KEY = "epos.chunkReloadAt";
  const last = Number(sessionStorage.getItem(KEY) || "0");
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

// The waiting worker is asked to activate, then we reload onto the new version the
// moment it takes control. A safety-net reload guarantees the "Update now" banner can
// never hang on "Updating…" — even if the controllerchange event is missed (which can
// happen with multiple tabs open or an already-claimed worker).
let swRegistration: ServiceWorkerRegistration | undefined;

function applyUpdate() {
  let done = false;
  const reload = () => { if (done) return; done = true; window.location.reload(); };
  navigator.serviceWorker?.addEventListener("controllerchange", reload);

  void (async () => {
    try {
      const reg = swRegistration ?? (await navigator.serviceWorker?.getRegistration()) ?? undefined;
      if (!reg) { reload(); return; }
      const skip = (w?: ServiceWorker | null) => w?.postMessage({ type: "SKIP_WAITING" });
      if (reg.waiting) {
        skip(reg.waiting);
        setTimeout(reload, 3000);
      } else if (reg.installing) {
        // A build is still downloading — apply the instant it finishes installing.
        const sw = reg.installing;
        sw.addEventListener("statechange", () => {
          if (sw.state === "installed") { skip(reg.waiting); setTimeout(reload, 3000); }
        });
      } else {
        await reg.update();
        skip(reg.waiting);
        setTimeout(reload, 3000);
      }
    } catch { setTimeout(reload, 3000); }
  })();
}

// Keep the installed PWA current WITHOUT ever reloading on its own. A new build is
// fetched quietly in the background; when it's ready we surface a small, dismissible
// banner so the user applies it exactly when it suits them. We never force a reload —
// an unprompted refresh interrupts data entry (e.g. half-way through a workshop job) and
// could discard an in-progress basket.
function applyWhenSafe() {
  showUpdatePrompt(applyUpdate);
}

registerSW({
  immediate: true,
  onNeedRefresh() {
    applyWhenSafe();
  },
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    swRegistration = registration;
    // A build may already be installed and waiting from a previous visit — apply/offer now.
    if (registration.waiting) applyWhenSafe();
    // Pre-fetch any new build when the app returns to the foreground (and hourly) so the
    // update is downloaded and ready to apply. This only downloads — it does not reload.
    const check = () => { if (document.visibilityState === "visible") void registration.update(); };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    setInterval(check, 60 * 60 * 1000);
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
